import { Analytics } from '@cobliteam/analytics';
import useVehicleGroup from '@cobliteam/hooks/src/useEntity/useVehicleGroup';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Input from '@cobliteam/ui/form/Input';
import { Check } from 'lucide-react';
import { useState } from 'react';

interface GroupStepBodyProps {
  fleetId?: string;
  /** Called after the group is created, to advance to the next step. */
  onCompleted?: () => void;
}

/** Linger after success so the user sees the confirmation before advancing. */
const ADVANCE_DELAY_MS = 900;

/** Inline "create a vehicle group" — just a name; vehicles can be added later on the groups page. */
const GroupStepBody = ({ fleetId, onCompleted }: GroupStepBodyProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const [name, setName] = useState('');
  const { mutateAsync: createGroup, isPending, isSuccess } = useVehicleGroup().createVehicleGroup;

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await createGroup({ name: trimmed, vehicleIds: [], userIds: [] });
    Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'groups' });
    window.setTimeout(() => onCompleted?.(), ADVANCE_DELAY_MS);
  };

  if (isSuccess) {
    return (
      <Typography variant='text-sm' className='flex items-center gap-1 text-success'>
        <Check size={16} aria-hidden />
        {t('inline.group_created')}
      </Typography>
    );
  }

  return (
    <div className='flex items-end gap-2'>
      <Input className='flex-1' value={name} onChange={(event) => setName(event.target.value)} placeholder={t('inline.group_name')} />
      <Button loading={isPending} disabled={!name.trim()} onClick={handleCreate}>
        {t('inline.create_group')}
      </Button>
    </div>
  );
};

export default GroupStepBody;
